package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.p2p.webrtc.DataChannelAdapter
import com.oasisfeng.invaluable.p2p.webrtc.DataChannelSession
import com.oasisfeng.invaluable.p2p.webrtc.PeerConnectionAdapter
import com.oasisfeng.invaluable.p2p.webrtc.SdpDescription
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration.Companion.milliseconds

typealias SignalingBlobCallback = (blobJson: String) -> Unit

/**
 * High-level manager for WebRTC peer connections using copy/paste (non-trickle) signaling.
 *
 * Encapsulates the complete offer → answer → apply-answer lifecycle, delegating platform
 * differences to [PeerConnectionAdapter] and [DataChannelAdapter]. Both TUI and Web clients
 * use this class instead of directly managing PeerConnection internals.
 *
 * Transport-only: each opened session is handed to [onSessionOpened], so replication stays above the offer/answer
 * signaling boundary.
 */
class WebRtcPeerManager(
    private val pcAdapter: PeerConnectionAdapter,
    private val dcAdapter: DataChannelAdapter,
    private val sessionTracker: SessionTracker,
    private val onSessionOpened: (DataChannelSession) -> Unit,
    var stunUrl: String = "",
    private val log: (String) -> Unit,
    private val onStateChanged: () -> Unit,
    private val gatheringTimeoutMs: Long = 10_000,
) {

    /**
     * Create an outbound offer. Returns the final signaling blob JSON.
     *
     * @param onBlobUpdate Optional incremental callback fired as ICE candidates are discovered.
     */
    suspend fun createOffer(onBlobUpdate: SignalingBlobCallback? = null): String {
        log("RTC creating offer...")
        val pc = pcAdapter.createPeerConnection(stunUrl, role = "offerer")
        val localCandidates = mutableListOf<dynamic>()
        var sdp: SdpDescription? = null
        val completion = CompletableDeferred<String>()

        pcAdapter.onLocalCandidate(pc) { candidate, mid ->
            val item = js("{}")
            item.candidate = candidate
            item.sdpMid = mid
            localCandidates.add(item)
            sdp?.let { onBlobUpdate?.invoke(encodeBlob(it, localCandidates)) }
        }

        pcAdapter.onGatheringStateChange(pc) { state ->
            log("RTC gathering state: $state")
            if (state == "complete") sdp?.let { completion.complete(encodeBlob(it, localCandidates)) }
        }

        pcAdapter.onConnectionStateChange(pc) { state ->
            log("RTC(offer) connectionState=$state")
            onStateChanged()
        }

        val dc = pcAdapter.createDataChannel(pc, "p2p")
        val session = DataChannelSession(
            id = sessionTracker.nextId(), pc = pc, outbound = true,
            log = log, onStateChanged = onStateChanged,
            adapter = dcAdapter,
        ).also { it.attachDataChannel(dc) }
        sessionTracker.add(session)
        onSessionOpened(session)

        // WebRTC standard says createDataChannel must be called BEFORE createOffer+setLocalDescription.
        // Node: SDP already triggered by createDataChannel; this awaits the onLocalDescription callback.
        sdp = pcAdapter.createOfferSdp(pc)
        onBlobUpdate?.invoke(encodeBlob(sdp, localCandidates))

        // node-datachannel can finish gathering before `sdp` is assigned, so the state-change handler's `sdp?.let{}`
        // saw null and never completed. Complete now if gathering already finished, else the await wastes the timeout.
        if (pcAdapter.getGatheringState(pc) == "complete") completion.complete(encodeBlob(sdp, localCandidates))
        return withTimeoutOrNull(gatheringTimeoutMs.milliseconds) { completion.await() } ?: encodeBlob(sdp, localCandidates)
    }

    suspend fun acceptOffer(offerBlob: String, onBlobUpdate: SignalingBlobCallback? = null): String {
        log("RTC accepting offer...")
        val offer = decodeBlob(offerBlob)
        val pc = pcAdapter.createPeerConnection(stunUrl, role = "answerer")
        val localCandidates = mutableListOf<dynamic>()
        var sdp: SdpDescription? = null

        val completion = CompletableDeferred<String>()

        pcAdapter.onLocalCandidate(pc) { candidate, mid ->
            val item = js("{}")
            item.candidate = candidate
            item.sdpMid = mid
            localCandidates.add(item)
            sdp?.let { onBlobUpdate?.invoke(encodeBlob(it, localCandidates)) }
        }

        pcAdapter.onGatheringStateChange(pc) { state ->
            log("RTC gathering state: $state")
            if (state == "complete") sdp?.let { completion.complete(encodeBlob(it, localCandidates)) }
        }

        pcAdapter.onConnectionStateChange(pc) { state ->
            log("RTC(answer) connectionState=$state")
            onStateChanged()
        }

        val session = DataChannelSession(
            id = sessionTracker.nextId(), pc = pc, outbound = false,
            log = log, onStateChanged = onStateChanged,
            adapter = dcAdapter,
        )
        sessionTracker.add(session)
        onSessionOpened(session)

        pcAdapter.onDataChannel(pc) { channel ->
            session.attachDataChannel(channel)
        }

        // Create answer (on node-datachannel, this also sets the remote offer)
        sdp = pcAdapter.createAnswerSdp(pc, offer.type as String, offer.sdp as String)

        // Add offer candidates (index-based: Kotlin/JS for-in over a raw JS array is unreliable).
        val offerCandidates = offer.candidates
        if (offerCandidates != null) {
            val len = offerCandidates.length as Int
            for (i in 0 until len) {
                val c = offerCandidates[i]
                pcAdapter.addRemoteCandidate(pc, c.candidate as String, c.sdpMid as String)
            }
        }

        onBlobUpdate?.invoke(encodeBlob(sdp, localCandidates))

        if (pcAdapter.getGatheringState(pc) == "complete") completion.complete(encodeBlob(sdp, localCandidates))
        return withTimeoutOrNull(gatheringTimeoutMs.milliseconds) { completion.await() } ?: encodeBlob(sdp, localCandidates)
    }

    suspend fun applyAnswer(answerBlob: String) {
        log("RTC applying answer...")
        val answer: dynamic = JSON.parse(answerBlob)
        // Find the most recent pending session (outbound)
        val sessions = sessionTracker.sessions
        var session: DataChannelSession? = null
        for (s in sessions) {
            if (s.outbound && !s.isOpen && !s.isClosed) {
                session = s
            }
        }
        if (session == null) error("No pending outbound session found")

        val pc = session.pc
        pcAdapter.setRemoteDescription(pc, answer.type as String, answer.sdp as String)
        val candidates = answer.candidates
        if (candidates != null) {
            val len = candidates.length as Int
            for (i in 0 until len) {
                val c = candidates[i]
                pcAdapter.addRemoteCandidate(pc, c.candidate as String, c.sdpMid as String)
            }
        }
    }

    companion object {
        private fun decodeBlob(blobJson: String): dynamic {
            return JSON.parse(blobJson)
        }

        private fun encodeBlob(desc: SdpDescription, candidates: List<dynamic>): String {
            val blob = js("{}")
            blob.type = desc.type
            blob.sdp = desc.sdp
            blob.candidates = candidates.toTypedArray()
            return JSON.stringify(blob)
        }
    }
}
